"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EVENT_ENTITY_LABELS = exports.EVENT_SENTENCES = void 0;
exports.eventEntityLabel = eventEntityLabel;
exports.eventTypeSentence = eventTypeSentence;
exports.eventSentence = eventSentence;
exports.eventTypesMatching = eventTypesMatching;
const jobs_1 = require("./jobs");
// Admin redesign 2026-11-02, Event log A: every event in a plain sentence ("Moved to Interview"), with the technical
// name kept beside it in small code letters. The map covers the types people meet every day; anything it doesn't
// name still reads as words ("interview.panelist_substituted" → "Interview panelist substituted"), never as a code,
// so a slice that adds an event type shows up readable without anyone registering it here.
/** Plain sentences, sentence case, no full stop (06-qol §11). Past tense: each row says what happened. */
exports.EVENT_SENTENCES = {
    // --- Sign-in and access
    'auth.login_success': 'Signed in',
    'auth.login_failed': 'Sign-in failed',
    'auth.logout': 'Signed out',
    'auth.reset_requested': 'Asked for a password reset',
    'auth.password_changed': 'Changed their password',
    'auth.2fa_enrolled': 'Turned on two-factor sign-in',
    'auth.2fa_verified': 'Passed the two-factor check',
    'auth.2fa_failed': 'Failed the two-factor check',
    'auth.account_locked': 'Account locked after wrong passwords',
    'auth.account_unlocked': 'Account unlocked',
    'auth.impersonation_started': 'Started viewing as someone',
    'auth.impersonation_ended': 'Stopped viewing as someone',
    'auth.session_revoked': 'Session ended',
    'auth.permission_denied': 'Refused an action',
    'audit.viewed': 'Opened the access log',
    'audit.exported': 'Exported the access log',
    'event_log.exported': 'Exported the event log',
    // --- People, roles and cover
    'user.invited': 'Invited to Nest Recruit',
    'user.invite_resent': 'Invitation sent again',
    'user.invite_revoked': 'Invitation cancelled',
    'user.activated': 'Accepted the invitation',
    'user.updated': 'Account details changed',
    'user.role_assigned': 'Role given',
    'user.role_removed': 'Role taken away',
    'user.deactivated': 'Account deactivated',
    'user.reactivated': 'Account reactivated',
    'user.suspended': 'Account suspended',
    'user.unsuspended': 'Account unsuspended',
    'user.password_reset_sent': 'Password reset link sent',
    'user.guest_panelist_added': 'Guest interviewer added',
    'ownership.transferred': 'Work handed over to someone else',
    'availability.updated': 'Free times changed',
    'role.created': 'Role created',
    'role.updated': 'Role changed',
    'role.deleted': 'Role deleted',
    'role.field_rule_changed': 'Hidden fields changed on a role',
    'role.transition_rule_changed': 'Special actions changed on a role',
    'delegation.created': 'Cover arranged',
    'delegation.ended': 'Cover ended',
    'delegation.dates_changed': 'Cover dates changed',
    // --- Candidates
    'candidate.created': 'Candidate added',
    'candidate.merged': 'Duplicate records merged',
    'candidate.unmerged': 'Merge undone',
    'candidate.duplicate_flagged': 'Flagged as a possible duplicate',
    'candidate.duplicate_dismissed': 'Marked as not a duplicate',
    'candidate.duplicate_deferred': 'Duplicate check put off',
    'candidate.dnr_set': 'Marked do not rehire',
    'candidate.dnr_cleared': 'Do not rehire taken off',
    'candidate.anonymised': 'Personal details anonymised',
    'candidate.retention_kept': 'Kept past the retention date',
    'candidate.retention_released': 'No longer kept past the retention date',
    'candidate.application_linked': 'Application linked to this candidate',
    'candidate.starred': 'Starred',
    'candidate.unstarred': 'Star taken off',
    'candidate.tag_added': 'Tag added',
    'candidate.tag_removed': 'Tag taken off',
    'custom_field.values_updated': 'Custom fields filled in',
    'document.uploaded': 'Document uploaded',
    'document.downloaded': 'Document downloaded',
    'document.download_failed': 'Document download failed',
    'document.text_extracted': 'CV text read',
    'document.text_unreadable': "CV text couldn't be read",
    'document.verified': 'Document verified',
    'document.rejected': 'Document rejected',
    'document.under_review': 'Document sent for review',
    'document.expired': 'Document expired',
    // --- Applications
    'application.created': 'Applied',
    'application.stage_changed': 'Moved to a new stage',
    'application.stage_skipped': 'Skipped a stage',
    'application.move_undone': 'Move undone',
    'application.scored': 'Score worked out',
    'application.rescored': 'Score worked out again',
    'application.rejected': 'Rejected',
    'application.auto_rejected': 'Rejected automatically',
    'application.rejection_reversed': 'Rejection undone',
    'application.reopened': 'Application reopened',
    'application.restored': 'Application restored',
    'application.closed_job': 'Closed when the job closed',
    'application.transferred': 'Moved to another job',
    'application.joined': 'Joined the company',
    'application.held': 'Put on hold',
    'application.hold_extended': 'Hold extended',
    'application.hold_released': 'Hold released',
    'application.hold_undone': 'Hold undone',
    'application.hold_expiring_sent': 'Reminder sent: hold ending soon',
    'application.reviewed': 'Reviewed',
    'application.review_deferred': 'Review put off',
    'application.review_undone': 'Review undone',
    'application.shortlisted': 'Shortlisted',
    'application.recruiter_assigned': 'Recruiter assigned',
    'application.source_corrected': 'Source corrected',
    'application.criterion_corrected': 'Ranking line corrected',
    'application.contact_status_changed': 'Contact status changed',
    'application.phone_screen_entered': 'Phone screen started',
    'application.phone_screen_passed': 'Passed the phone screen',
    'application.phone_screen_pass_undone': 'Phone screen pass undone',
    'application.phone_screen_sent_to_contact': 'Sent on to Contact after the phone screen',
    'application.phone_screen_send_undone': 'Send to Contact undone',
    'screening.session_started': 'Sorting session started',
    'screening.session_ended': 'Sorting session ended',
    'screening.card_viewed': 'Looked at in sorting',
    'screening.card_held': 'Held in sorting',
    'screening.decision_recorded': 'Sorting decision made',
    'screening.decision_undone': 'Sorting decision undone',
    'shortlist.sent': 'Shortlist sent to the manager',
    'shortlist.answered': 'Manager answered the shortlist',
    'shortlist.completed': 'Shortlist finished',
    'shortlist.reminded': 'Manager reminded about the shortlist',
    'shortlist.hr_proceeded': 'HR went ahead without the manager',
    'comparison.added': 'Added to the comparison',
    'comparison.removed': 'Taken out of the comparison',
    'comparison.restored': 'Put back in the comparison',
    'decision.recorded': 'Round decision recorded',
    'decision.ready': 'Ready for a decision',
    'decision.override': 'Decision overridden',
    'decision.tie_broken': 'Tie broken',
    'decision.final_selection': 'Selected for the job',
    'decision.final_selection_undone': 'Selection undone',
    'decision.overdue_sent': 'Reminder sent: decision overdue',
    'bulk_action.executed': 'Bulk action run',
    'bulk_action.undone': 'Bulk action undone',
    // --- Jobs and hire requests
    'job.draft_created': 'Job draft started',
    'job.draft_saved': 'Job draft saved',
    'job.opened': 'Job opened',
    'job.held': 'Job put on hold',
    'job.resumed': 'Job resumed',
    'job.closed': 'Job closed',
    'job.cancelled': 'Job cancelled',
    'job.filled': 'Job filled',
    'job.reopened': 'Job reopened',
    'job.cloned': 'Job copied',
    'job.copy_undone': 'Job copy undone',
    'job.content_edited': 'Job details changed',
    'job.config_changed': 'Job set-up changed',
    'job.criteria_set': 'Ranking criteria set',
    'job.knockouts_set': 'Knock-out questions set',
    'job.pipeline_set': 'Job stages set',
    'job.round_defined': 'Interview round set up',
    'job.panel_assigned': 'Interview panel chosen',
    'job.round_kit_set': 'Interview kit set on a round',
    'job.interview_plan_applied': 'Interview plan copied into the job',
    'job.template_applied': 'Role template applied',
    'job.rescored': 'Every application re-ranked',
    'job.ranking_moved_to_profile': 'Ranking moved to the ideal candidate',
    'job.brief_updated': 'Job brief changed',
    'job.ideal_profile_updated': 'Ideal candidate profile changed',
    'job.form_configured': 'Application form set up',
    'job.offer_config_set': 'Offer settings changed',
    'job.assessment_attached': 'Test attached',
    'job.assessment_count_set': 'Number of tests set',
    'job.assessment_settings_set': 'Test settings changed',
    'job.file_attached': 'File attached to the job',
    'job.file_removed': 'File taken off the job',
    'job.image_requested': 'Job image asked for',
    'job.image_made': 'Job image made',
    'job.image_failed': "Job image couldn't be made",
    'job.image_restored': 'Earlier job image put back',
    'job.post_words_saved': 'Job post wording saved',
    'job.post_words_reset': 'Job post wording reset',
    'job.post_design_saved': 'Job post design saved',
    'job.post_design_reset': 'Job post design reset',
    'job.post_reference_added': 'Reference picture added',
    'job.post_reference_removed': 'Reference picture taken off',
    'job.jev_budget_set': 'Jev budget set',
    'job.jev_budget_raised': 'Jev budget raised',
    'job.jev_budget_asked': 'More Jev budget asked for',
    'job.jev_budget_warned': 'Jev budget running low',
    'job.jev_budget_used_up': 'Jev budget used up',
    // `job.*` below is the background-work queue, not a job opening: the worker writes these.
    'job.completed': 'Background work finished',
    'job.failed': 'Background work failed',
    'job_opening.phone_screen_switched': 'Phone screen switched on or off',
    'requisition.created': 'Hire request created',
    'requisition.updated': 'Hire request changed',
    'requisition.content_edited': 'Hire request details changed',
    'requisition.submitted': 'Hire request sent for approval',
    'requisition.routed': 'Hire request routed to its approvers',
    'requisition.step_approved': 'Approval step approved',
    'requisition.step_reassigned': 'Approval step passed to someone else',
    'requisition.approved': 'Hire request approved',
    'requisition.rejected': 'Hire request rejected',
    'requisition.sent_back': 'Hire request sent back for changes',
    'requisition.revised': 'Hire request revised',
    'requisition.held': 'Hire request put on hold',
    'requisition.resumed': 'Hire request resumed',
    'requisition.cancelled': 'Hire request cancelled',
    'requisition.filled': 'Hire request filled',
    'requisition.partially_filled': 'Hire request partly filled',
    'requisition.recruiter_assigned': 'Recruiter assigned to the hire request',
    'requisition.chased': 'Approver reminded',
    'requisition.backfill_drafted': 'Replacement hire request drafted',
    'posting.published': 'Job post published',
    'posting.unpublished': 'Job post taken down',
    'posting.scheduled': 'Job post scheduled',
    'posting.updated': 'Job post changed',
    'posting.expired': 'Job post expired',
    'posting.auto_closed': 'Job post closed automatically',
    'posting.viewed': 'Job post viewed',
    'cost.recorded': 'Advertising cost recorded',
    // --- Tests
    'assessment.created': 'Test created',
    'assessment.updated': 'Test changed',
    'assessment.archived': 'Test archived',
    'assessment.assigned': 'Test sent',
    'assessment.started': 'Test started',
    'assessment.submitted': 'Test handed in',
    'assessment.auto_scored': 'Test marked automatically',
    'assessment.graded': 'Test marked',
    'assessment.answer_marked': 'Answer marked',
    'assessment.result_entered': 'Test result entered',
    'assessment.result_recorded': 'Test result recorded',
    'assessment.grading_assigned': 'Marking given to someone',
    'assessment.grading_chased': 'Marker reminded',
    'assessment.reminder_sent': 'Test reminder sent',
    'assessment.expired': 'Test link expired',
    'assessment.cancelled': 'Test cancelled',
    'assessment.no_show': "Didn't take the test",
    'assessment.link_copied': 'Test link copied',
    'exam_session.created': 'Test session created',
    'exam_session.scheduled': 'Test session scheduled',
    'exam_session.rescheduled': 'Test session moved',
    'exam_session.updated': 'Test session changed',
    'exam_session.cancelled': 'Test session cancelled',
    'exam_session.started': 'Test session started',
    'exam_session.completed': 'Test session finished',
    'exam_session.attendance_marked': 'Attendance marked',
    'exam_session.roster_updated': 'Test session list changed',
    'exam_session.roster_locked': 'Test session list locked',
    'question.created': 'Question added',
    'question.updated': 'Question changed',
    'question.archived': 'Question archived',
    // --- Interviews and scorecards
    'interview.created': 'Interview created',
    'interview.scheduled': 'Interview booked',
    'interview.rescheduled': 'Interview moved',
    'interview.confirmed': 'Interview confirmed',
    'interview.cancelled': 'Interview cancelled',
    'interview.completed': 'Interview finished',
    'interview.candidate_no_show': 'Candidate did not attend',
    'interview.panelist_no_show': 'Interviewer did not attend',
    'interview.panelist_changed': 'Interviewer changed',
    'interview.panelist_substituted': 'Interviewer replaced',
    'interview.panelist_unavailable': 'Interviewer said they are not free',
    'interview.panel_synced': 'Interview panel updated from the round',
    'interview.kit_set': 'Interview kit updated from the round',
    'interview.slots_offered': 'Interview times offered',
    'interview.slot_reminder_sent': 'Reminder sent: pick an interview time',
    'interview.reminder_sent': 'Interview reminder sent',
    'interview.room_set': 'Interview room set',
    'interview.link_set': 'Interview video link set',
    'interview.mode_changed': 'Interview changed to in person or online',
    'interview.notes_set': 'Interview notes written',
    'interview.flagged': 'Interview flagged',
    'interview.flag_removed': 'Interview flag taken off',
    'interview.message_copied': 'Interview message copied',
    'interview.stranded': 'Interview left without a time',
    'scorecard.opened': 'Scorecard opened',
    'scorecard.started': 'Scorecard started',
    'scorecard.draft_saved': 'Scorecard draft saved',
    'scorecard.submitted': 'Scorecard handed in',
    'scorecard.edited': 'Scorecard changed after handing in',
    'scorecard.due_set': 'Scorecard due date set',
    'scorecard.overdue_sent': 'Reminder sent: scorecard overdue',
    'scorecard.prior_viewed': "Looked at earlier rounds' scores",
    'scorecard.synced': 'Scorecard updated',
    'scorecard_link.sent': 'Scorecard link sent',
    'scorecard_link.opened': 'Scorecard link opened',
    'scorecard_link.submitted': 'Scorecard handed in through its link',
    'scorecard_link.reminder_sent': 'Scorecard link reminder sent',
    'scorecard_link.revoked': 'Scorecard link stopped',
    'scorecard_link.expired': 'Scorecard link expired',
    'rubric.version_created': 'New rubric version saved',
    // --- Offers and hires
    'offer.submitted': 'Offer sent for approval',
    'offer.updated': 'Offer terms changed',
    'offer.step_approved': 'Offer approval step approved',
    'offer.step_rejected': 'Offer approval step rejected',
    'offer.step_reassigned': 'Offer approval step passed to someone else',
    'offer.approval_chased': 'Offer approver reminded',
    'offer.approval_invalidated': 'Offer approval no longer stands',
    'offer.approved': 'Offer approved',
    'offer.sent': 'Offer sent',
    'offer.viewed': 'Offer opened by the candidate',
    'offer.revised': 'Offer being revised',
    'offer.accepted': 'Offer accepted',
    'offer.declined': 'Offer declined',
    'offer.withdrawn': 'Offer withdrawn',
    'offer.expired': 'Offer expired',
    'offer.expiring_sent': 'Reminder sent: offer ending soon',
    'offer.accepted_no_show': "Accepted but didn't join",
    'offer.joined': 'Joined',
    'offer.joining_soon_sent': 'Reminder sent: joining soon',
    'offer.joining_overdue_sent': 'Reminder sent: joining date passed',
    'offer.pdf_generated': 'Offer letter made',
    'offer.signed_copy_uploaded': 'Signed offer uploaded',
    'offer.documents_chased': 'Joining documents asked for again',
    'offer.message_copied': 'Offer message copied',
    'offer.upload_link_copied': 'Upload link copied',
    'offer_letter.created': 'Offer letter template created',
    'offer_letter.updated': 'Offer letter template changed',
    'employee.created': 'Employee record created',
    'employee.exit_announced': 'Leaving date recorded',
    'employee.summary_copied': 'Hire summary copied',
    'hire_plans.requested': '30 and 90 day plans asked for',
    'hire_plans.saved': '30 and 90 day plans saved',
    // --- Organisation
    'org_unit.created': 'Part of the organisation added',
    'org_unit.updated': 'Part of the organisation changed',
    'org_unit.archived': 'Part of the organisation archived',
    'position.created': 'Seat created',
    'position.updated': 'Seat changed',
    'position.status_changed': 'Seat status changed',
    'position.planned': 'Seat planned',
    'position.filled': 'Seat filled',
    'position.frozen': 'Seat frozen',
    'position.unfrozen': 'Seat unfrozen',
    'position.reassigned': 'Seat moved',
    'position.archived': 'Seat archived',
    'position.restored': 'Seat restored',
    'headcount_plan.set': 'Headcount plan set',
    'calendar.created': 'Calendar created',
    'calendar.updated': 'Calendar changed',
    'calendar.archived': 'Calendar archived',
    'calendar.holiday_added': 'Holiday added',
    'calendar.holiday_removed': 'Holiday taken off',
    'calendar.assigned_to_user': 'Calendar given to someone',
    'resource.created': 'Room added',
    'resource.updated': 'Room changed',
    'resource.archived': 'Room taken off',
    'resource.booked': 'Room booked',
    'resource.released': 'Room booking freed',
    // --- Pools, tags and messages
    'pool.created': 'Talent pool created',
    'pool.updated': 'Talent pool changed',
    'pool.archived': 'Talent pool archived',
    'pool.merged': 'Talent pools merged',
    'pool.member_added': 'Added to a talent pool',
    'pool.member_removed': 'Taken out of a talent pool',
    'pool.membership_expired': 'Talent pool place ran out',
    'pool.invited': 'Invited from a talent pool',
    'pool.match_run': 'Talent pools checked for matches',
    'pool.match_threshold_changed': 'Talent pool match level changed',
    'pool.match_read': 'Talent pool matches read',
    'consent.renewed': 'Consent renewed',
    'tag.created': 'Tag created',
    'cv_bank.added': 'Added to the CV bank',
    'cv_bank.removed': 'Taken out of the CV bank',
    'cv_bank.duplicate_resolved': 'CV bank duplicate sorted out',
    'message.queued': 'Email queued',
    'message.sent': 'Email sent',
    'message.received_unmatched': 'Email received, not matched to anyone',
    'message.assigned': 'Email matched to a candidate',
    'message.dismissed': 'Email dismissed',
    'activity.created': 'Note or call logged',
    'activity.updated': 'Note or call changed',
    'task.created': 'Task created',
    'task.completed': 'Task done',
    'task.reopened': 'Task reopened',
    'task.reassigned': 'Task given to someone else',
    'task.question_asked': 'Question asked as a task',
    'template.created': 'Message template created',
    'template.updated': 'Message template changed',
    'template.submitted': 'Message template sent for approval',
    'template.approved': 'Message template approved',
    'template.rejected': 'Message template rejected',
    'template.certified': 'Message template certified',
    'template.uncertified': 'Message template certification taken off',
    'notification.sent': 'Notification sent',
    'notification.read': 'Notifications read',
    'notification.digest_sent': 'Daily summary sent',
    'notification.prefs_updated': 'Notification settings changed',
    'escalation.scheduled': 'Escalation scheduled',
    'escalation.fired': 'Escalated',
    // --- Time limits
    'sla.started': 'Time limit started',
    'sla.paused': 'Time limit paused',
    'sla.resumed': 'Time limit resumed',
    'sla.stopped': 'Time limit stopped',
    'sla.warning': 'Time limit nearly up',
    'sla.breached': 'Time limit passed',
    'sla_rule.updated': 'Time limit rule changed',
    // --- Settings
    'settings.module_toggled': 'Part switched on or off',
    'config.system_updated': 'System settings changed',
    'config.pipeline_created': 'Pipeline created',
    'config.pipeline_updated': 'Pipeline changed',
    'config.rubric_created': 'Rubric created',
    'config.rubric_updated': 'Rubric changed',
    'config.kit_created': 'Interview kit created',
    'config.kit_updated': 'Interview kit changed',
    'config.plan_created': 'Interview plan created',
    'config.plan_updated': 'Interview plan changed',
    'config.approval_chain_created': 'Approval chain created',
    'config.approval_chain_updated': 'Approval chain changed',
    'config.sources_updated': 'Sources changed',
    'config.custom_field_updated': 'Custom field changed',
    'config.career_site_updated': 'Career site changed',
    'config.retention_updated': 'Retention rules changed',
    'config.fx_rates_updated': 'Exchange rates changed',
    'config.name_equivalences_updated': 'Name matching changed',
    'metric_dictionary.updated': 'Metric dictionary changed',
    'retention.archive_sent': 'Retention archive sent',
    'retention.archive_failed': "Retention archive couldn't be sent",
    'tool_name.added': 'Tool name added',
    'tool_name.hidden': 'Tool name deleted',
    'tool_name.restored': 'Tool name put back',
    'tool_name.merged': 'Tool names merged',
    'venture.post_design_default_saved': "Venture's job post design saved",
    'criteria_library.saved': 'Criterion saved to the library',
    'criteria_library.replaced': 'Library criterion replaced',
    'criteria_library.removed': 'Criterion taken out of the library',
    // --- Reports, views and the rest
    'report.saved': 'Report saved',
    'report.deleted': 'Report deleted',
    'report.exported': 'Report exported',
    'report.scheduled': 'Report scheduled',
    'view.saved': 'View saved',
    'view.deleted': 'View deleted',
    'preference.updated': 'Preference changed',
    'preference.removed': 'Preference reset',
    'search.executed': 'Searched',
    'import.uploaded': 'Import file uploaded',
    'import.mapped': 'Import columns matched',
    'import.started': 'Import started',
    'import.finished': 'Import finished',
    'import.cancelled': 'Import cancelled',
    'import.discarded': 'Import thrown away',
    'import.row_failed': "Import row couldn't be added",
    'import.row_skipped': 'Import row skipped',
    'icp.profile_drafted': 'Ideal candidate drafted',
    'icp.profile_saved': 'Ideal candidate saved',
    'icp.focus_changed': 'Ideal candidate focus changed',
    'icp.interview_started': 'Ideal candidate questions started',
    'icp.interview_answered': 'Ideal candidate question answered',
    'icp.question_asked': 'Ideal candidate question asked',
    'jev.ask_run': 'Jev asked to read',
    'jev.ask_finished': 'Jev finished reading',
    'jev.reasons_written': 'Jev wrote its reasons',
    'jev.finding_corrected': "Jev's finding corrected",
    'jev.correction_withdrawn': "Correction to Jev's finding taken back",
    'jev.reading_stopped_for_budget': 'Jev stopped: the budget ran out',
    'jev.reading_resumed_after_budget': 'Jev carried on after the budget was raised',
    'hm.action_timed': "Manager's action timed",
    'phone_screen.round_added': 'Phone screen round added',
    'phone_screen.round_changed': 'Phone screen round changed',
    'phone_screen.round_removed': 'Phone screen round taken off',
};
/** What each kind of record is called on screen. A kind missing here still reads as words. */
exports.EVENT_ENTITY_LABELS = {
    application: 'Application',
    candidate: 'Candidate',
    job_opening: 'Job',
    requisition: 'Hire request',
    interview: 'Interview',
    offer: 'Offer',
    position: 'Seat',
    posting: 'Job post',
    user: 'Person',
    role: 'Role',
    invite: 'Invitation',
    delegation: 'Cover',
    session: 'Session',
    audit: 'Access log',
    event_log: 'Event log',
    venture: 'Venture',
    department: 'Department',
    team: 'Team',
    employee: 'Employee',
    document: 'Document',
    message: 'Email',
    message_template: 'Message template',
    activity: 'Note or call',
    task: 'Task',
    talent_pool: 'Talent pool',
    pool_membership: 'Talent pool place',
    assessment: 'Test',
    assessment_attempt: 'Test attempt',
    exam_session: 'Test session',
    question: 'Question',
    work_calendar: 'Calendar',
    room: 'Room',
    job_queue: 'Background work',
    sla_clock: 'Time limit',
    sla_rule: 'Time limit rule',
    approval_chain: 'Approval chain',
    approval_step: 'Approval step',
    screening_session: 'Sorting session',
    saved_report: 'Saved report',
    saved_view: 'Saved view',
    user_preference: 'Preference',
    organisation_settings: 'System settings',
    retention_settings: 'Retention rules',
    archive_export: 'Retention archive',
    fx_rate: 'Exchange rate',
    custom_field: 'Custom field',
    source: 'Source',
    rubric: 'Rubric',
    interview_kit: 'Interview kit',
    interview_plan_template: 'Interview plan',
    pipeline_template: 'Pipeline',
    role_template: 'Role template',
    pipeline_action: 'Bulk action',
    jev_ask: 'Jev reading',
    offer_letter: 'Offer letter',
    hire_plans: '30 and 90 day plans',
    cost_entry: 'Advertising cost',
    metric_dictionary: 'Metric dictionary',
    name_equivalences: 'Name matching',
    career_site: 'Career site',
    import: 'Import',
    import_row: 'Import row',
    shortlist: 'Shortlist',
    phone_screen_round: 'Phone screen round',
    notification: 'Notification',
    workspace: 'Workspace',
    command_center: 'Command centre',
    search: 'Search',
    tag: 'Tag',
    employee_exit: 'Leaving record',
    headcount_plan: 'Headcount plan',
    saved_criterion: 'Saved criterion',
    screening_decision: 'Sorting decision',
};
/** "panelist_substituted" → "panelist substituted"; "2fa" stays as written. */
function wordsOf(text) {
    return text
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_.-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}
function capitalised(text) {
    return text === '' ? text : text.charAt(0).toUpperCase() + text.slice(1);
}
/** A kind of record in plain words: "Application", "Hire request", or the kind's own name as words. */
function eventEntityLabel(entityType) {
    return exports.EVENT_ENTITY_LABELS[entityType] ?? (capitalised(wordsOf(entityType)) || 'Record');
}
/**
 * The plain sentence for an event type. Unknown types read as "{record} {what happened}"
 * ("interview.panelist_substituted" → "Interview panelist substituted"), never as a code.
 */
function eventTypeSentence(eventType) {
    const known = exports.EVENT_SENTENCES[eventType];
    if (known)
        return known;
    const dot = eventType.indexOf('.');
    if (dot <= 0)
        return capitalised(wordsOf(eventType)) || 'Something happened';
    const entity = exports.EVENT_ENTITY_LABELS[eventType.slice(0, dot)]?.toLowerCase() ?? wordsOf(eventType.slice(0, dot));
    const action = wordsOf(eventType.slice(dot + 1));
    return capitalised(`${entity} ${action}`.trim()) || 'Something happened';
}
function valueOf(payload, key) {
    return typeof payload === 'object' && payload !== null && !Array.isArray(payload) ? payload[key] : undefined;
}
function isStage(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(jobs_1.PIPELINE_STAGE_LABELS, value);
}
/** Stages that read badly as "Moved to …": the end of the road, or a pause. */
const STAGE_SENTENCES = {
    rejected: 'Rejected',
    auto_rejected: 'Rejected automatically',
    withdrawn: 'Withdrew',
    closed_job: 'Closed when the job closed',
    hold: 'Put on hold',
    joined: 'Joined the company',
    transferred: 'Moved to another job',
};
function stageSentence(stage) {
    return STAGE_SENTENCES[stage] ?? `Moved to ${jobs_1.PIPELINE_STAGE_LABELS[stage]}`;
}
/**
 * The sentence for one row, with the detail that makes it useful where the event carries it:
 * "Moved to Interview 1", "Score worked out: 82". Anything missing or hidden falls back to the type's sentence.
 */
function eventSentence(source) {
    const after = source.payloadAfter;
    if (source.eventType === 'application.stage_changed') {
        const stage = valueOf(after, 'stage');
        if (isStage(stage))
            return stageSentence(stage);
    }
    if (source.eventType === 'application.scored' || source.eventType === 'application.rescored') {
        const score = valueOf(after, 'score');
        if (typeof score === 'number' && Number.isFinite(score))
            return `${eventTypeSentence(source.eventType)}: ${Math.round(score)}`;
    }
    return eventTypeSentence(source.eventType);
}
/** The event types whose plain sentence contains the search, so "moved to interview" finds the stage changes. */
function eventTypesMatching(q) {
    const needle = q.trim().toLowerCase();
    if (needle === '')
        return [];
    return Object.keys(exports.EVENT_SENTENCES).filter((eventType) => exports.EVENT_SENTENCES[eventType]?.toLowerCase().includes(needle));
}
//# sourceMappingURL=event-log-labels.js.map